/**
 * StartPointSelector - Inline popover selector for chakra start point
 *
 * Features:
 * - Compact inline display with Sanskrit name
 * - Popover with badge grid on click
 * - Custom degree input with label
 */

import { devError } from '../../../../../utils/devLog';
import React, { useState } from 'react';
import {
  Box,
  Badge,
  Wrap,
  WrapItem,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  useDisclosure,
  Input,
  HStack,
  Button
} from '@chakra-ui/react';
import {
  ChakraStartPoint,
  START_POINT_INFO,
  CustomStartPointSettings,
  getStartPointOptions
} from '../../../../../config/divisionalChakraConfig';
import { JatakaResponse } from '../../../../../models/api';

export interface StartPointSelectorProps {
  currentStartPoint: ChakraStartPoint;
  onStartPointChange: (startPoint: ChakraStartPoint) => Promise<void>;
  customSettings?: CustomStartPointSettings;
  onCustomSettingsChange?: (settings: CustomStartPointSettings) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
  apiResponse?: JatakaResponse | null;
  chakraLabel?: string;
  chakraType?: string;
}

/**
 * Get available planets from API response for filtering
 */
function getAvailablePlanets(apiResponse?: JatakaResponse | null): Set<string> {
  const available = new Set<string>();

  if (apiResponse?.graha_positions?.planets) {
    apiResponse.graha_positions.planets.forEach(planet => {
      const planetName = planet.name.toLowerCase();
      available.add(planetName);
    });
  }

  if (apiResponse?.basic_info) {
    available.add('lagna');
  }

  if (apiResponse?.astronomical?.sunrise_surya_longitude) {
    available.add('sunrise');
  }

  return available;
}

/**
 * Get short label for chakra type (max 2 chars)
 * nakshatra → NA, muhurta → MU, d7 → 7, d20 → 20
 */
const getDefaultChakraLabel = (chakraType?: string): string => {
  if (!chakraType) return 'L1';
  if (chakraType === 'nakshatra') return 'NA';
  if (chakraType === 'muhurta') return 'MU';
  // d7 → 7, d20 → 20, d60 → 60
  const match = chakraType.match(/^d(\d+)$/i);
  if (match) return match[1].slice(0, 2);
  return 'L1';
};

/**
 * StartPointSelector Component
 * Inline popover for selecting chakra start point
 */
export const StartPointSelector: React.FC<StartPointSelectorProps> = ({
  currentStartPoint,
  onStartPointChange,
  customSettings = { degrees: 0, label: 'L1' },
  onCustomSettingsChange,
  isDisabled = false,
  isLoading = false,
  apiResponse,
  chakraType
}) => {
  const defaultLabel = getDefaultChakraLabel(chakraType);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const availablePlanets = getAvailablePlanets(apiResponse);

  // Local state for custom input - initialize from props
  const [customDegrees, setCustomDegrees] = useState(customSettings.degrees.toString());
  const [customLabel, setCustomLabel] = useState(customSettings.label);
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Sync local state when props change
  React.useEffect(() => {
    setCustomDegrees(customSettings.degrees.toString());
    setCustomLabel(customSettings.label);
  }, [customSettings.degrees, customSettings.label]);

  const handleBadgeClick = async (startPoint: ChakraStartPoint) => {
    if (isDisabled || isLoading) return;

    if (startPoint === 'custom') {
      // Reset label to default if current is too long (>2 chars)
      if (customLabel.length > 2) {
        setCustomLabel(defaultLabel);
      }
      setShowCustomInput(true);
      return;
    }

    if (startPoint === currentStartPoint) {
      onClose();
      return;
    }

    try {
      setShowCustomInput(false);
      await onStartPointChange(startPoint);
      onClose();
    } catch (error) {
      devError(`❌ StartPointSelector: Failed to change start point:`, error);
    }
  };

  const handleCustomApply = async () => {
    if (!onCustomSettingsChange) return;

    const degrees = parseFloat(customDegrees) || 0;
    const validDegrees = Math.max(0, Math.min(360, degrees));

    try {
      await onCustomSettingsChange({ degrees: validDegrees, label: customLabel || 'L1' });
      setShowCustomInput(false);
      onClose();
    } catch (error) {
      devError(`❌ StartPointSelector: Failed to apply custom settings:`, error);
    }
  };

  const currentInfo = START_POINT_INFO[currentStartPoint];
  // Display name for trigger
  const displayName = currentStartPoint === 'custom'
    ? `${customSettings.label}[${customSettings.degrees.toFixed(1)}°]`
    : currentInfo.label.split(' ')[0];

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-start">
      <PopoverTrigger>
        <Box
          as="button"
          display="flex"
          alignItems="center"
          gap={0.5}
          px={1}
          py={0.5}
          borderRadius="sm"
          bg="transparent"
          cursor={isDisabled ? 'default' : 'pointer'}
          opacity={isDisabled ? 0.5 : 1}
          _hover={{ bg: isDisabled ? 'transparent' : 'gray.100' }}
          transition="background 0.2s"
        >
          <Text fontSize="xs" fontWeight="medium" color={currentStartPoint === 'custom' ? 'blue.600' : 'gray.600'}>
            {displayName}
          </Text>
          <Text fontSize="2xs" color="gray.400">
            ▼
          </Text>
        </Box>
      </PopoverTrigger>

      <PopoverContent width="auto" maxW="260px" _focus={{ boxShadow: 'sm' }}>
        <PopoverBody p={2}>
          <Wrap spacing={1}>
            {getStartPointOptions().map(startPoint => {
              const info = START_POINT_INFO[startPoint];

              // Hide 'default' for all chakras except nakshatra
              if (startPoint === 'default' && chakraType !== 'nakshatra') {
                return null;
              }

              const isAvailable = startPoint === 'custom' || startPoint === 'default' || availablePlanets.has(startPoint);
              const isActive = startPoint === currentStartPoint;

              if (!isAvailable) return null;

              const label = startPoint === 'custom' ? 'Custom' : info.label.split(' ')[0];

              return (
                <WrapItem key={startPoint}>
                  <Badge
                    colorScheme={isActive ? (startPoint === 'custom' ? 'blue' : 'purple') : 'gray'}
                    variant={isActive ? 'solid' : 'outline'}
                    fontSize="xs"
                    px={1.5}
                    py={0.5}
                    borderRadius="sm"
                    cursor={isLoading ? 'wait' : 'pointer'}
                    onClick={() => handleBadgeClick(startPoint)}
                    _hover={{
                      bg: isActive ? undefined : 'gray.100',
                      transform: 'scale(1.02)'
                    }}
                    transition="all 0.15s"
                    fontWeight={isActive ? 'semibold' : 'normal'}
                  >
                    {label}
                  </Badge>
                </WrapItem>
              );
            })}
          </Wrap>

          {/* Custom input fields */}
          {showCustomInput && (
            <Box mt={2} pt={2} borderTop="1px solid" borderColor="gray.200">
              <HStack spacing={1} mb={1}>
                <Input
                  size="xs"
                  width="70px"
                  placeholder="0-360"
                  value={customDegrees}
                  onChange={(e) => setCustomDegrees(e.target.value)}
                  type="number"
                  step="0.1"
                  min="0"
                  max="360"
                />
                <Text fontSize="xs" color="gray.500">°</Text>
                <Input
                  size="xs"
                  width="45px"
                  placeholder={defaultLabel}
                  value={customLabel}
                  onChange={(e) => setCustomLabel(e.target.value.slice(0, 2))}
                  maxLength={2}
                />
                <Button size="xs" colorScheme="blue" onClick={handleCustomApply} isLoading={isLoading}>
                  OK
                </Button>
              </HStack>
            </Box>
          )}

          {isLoading && !showCustomInput && (
            <Text fontSize="2xs" color="gray.500" mt={1}>
              Updating...
            </Text>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
